import router from '@/router'
import { useNotificationStore } from '@/store/modules/notification'
import { useUserStore } from '@/store/modules/user'
import echo, { reconnectEcho } from '@/utils/echo'
import { ElNotification } from 'element-plus'

const PUBLIC_CHANNEL = 'announcements'

let subscribedPrivateChannel = ''
let listeningPrivate = false
let listeningPublic = false
let recoveringPrivateAuth = false
let lifecycleEventBound = false
let recoveringConnection = false
let boundConnection: any = null
let boundStateChangeHandler: ((states: any) => void) | null = null

export function useNotifications() {
  const notificationStore = useNotificationStore()
  const userStore = useUserStore()

  const pushNotification = (e: any, fallbackTitle = '系统通知', fallbackContent = '你收到一条新通知', fallbackType = '1') => {
    notificationStore.pushNotification({
      ...e,
      title: e?.title ?? fallbackTitle,
      content: e?.content ?? fallbackContent,
      type: e?.type ?? fallbackType,
      read: false,
    })
    ElNotification({
      title: '提示',
      message: h('span', null, [
        '您有一条新通知，',
        h(
          'span',
          {
            style: {
              color: '#409EFF',
            },
          },
          '点击查看',
        ),
      ]),
      type: 'warning',
      duration: 5000,
      customClass: 'cursor-pointer',
      onClick: () => {
        router.push({ name: 'routing-notification-center-message-list' }).catch(() => {})
      },
    })
  }

  const normalizeErrorStatus = (error: any) => {
    if (typeof error === 'number') {
      return error
    }
    return Number(error?.status ?? error?.statusCode ?? error?.error?.status ?? 0)
  }

  const subscribePublicChannel = () => {
    echo.channel(PUBLIC_CHANNEL)
      .listen('.announcement.received', (e: any) => {
        pushNotification(e, '系统通知', '你收到一条新通知', '1')
      })
  }

  const rebuildEchoAndResubscribe = async () => {
    if (recoveringConnection) {
      return
    }

    recoveringConnection = true
    try {
      reconnectEcho(userStore.token || localStorage.getItem('token') || '')

      if (listeningPublic) {
        echo.leave(PUBLIC_CHANNEL)
        subscribePublicChannel()
      }

      if (listeningPrivate && subscribedPrivateChannel) {
        echo.leave(subscribedPrivateChannel)
        subscribePrivateChannel(subscribedPrivateChannel)
      }
    }
    finally {
      setTimeout(() => {
        recoveringConnection = false
      }, 300)
    }
  }

  function subscribePrivateChannel(privateChannelName: string) {
    echo.private(privateChannelName)
      .error(async (error: any) => {
        const status = normalizeErrorStatus(error)
        if (recoveringPrivateAuth) {
          return
        }

        recoveringPrivateAuth = true
        try {
          if (status === 401) {
            await userStore.refreshToken()
            resubscribePrivateChannelOnly()
            return
          }
          await rebuildEchoAndResubscribe()
        }
        catch (refreshError) {
          console.error('私有频道鉴权失败，刷新token失败', refreshError)
          await userStore.requestLogout()
        }
        finally {
          recoveringPrivateAuth = false
        }
      })
      .listen('.notification.received', (e: any) => {
        pushNotification(e)
      })
  }

  function resubscribePrivateChannelOnly() {
    if (!listeningPrivate || !subscribedPrivateChannel) {
      return
    }

    echo.leave(subscribedPrivateChannel)
    subscribePrivateChannel(subscribedPrivateChannel)
  }

  const bindWsLifecycleEvents = () => {
    const connector = echo.connector as any
    const connection = connector?.pusher?.connection
    if (!connection) {
      return
    }

    if (boundConnection && boundConnection !== connection && boundStateChangeHandler) {
      boundConnection.unbind('state_change', boundStateChangeHandler)
    }

    if (boundConnection === connection && boundStateChangeHandler) {
      return
    }

    boundStateChangeHandler = (states: any) => {
      const current = states?.current
      if (current === 'disconnected' || current === 'unavailable') {
        void rebuildEchoAndResubscribe()
      }
    }
    boundConnection = connection
    connection.bind('state_change', boundStateChangeHandler)
  }

  const stopNotificationListener = () => {
    if (subscribedPrivateChannel) {
      echo.leave(subscribedPrivateChannel)
    }
    if (listeningPublic) {
      echo.leave(PUBLIC_CHANNEL)
    }
    subscribedPrivateChannel = ''
    listeningPrivate = false
    listeningPublic = false
    recoveringPrivateAuth = false
    recoveringConnection = false

    if (boundConnection && boundStateChangeHandler) {
      boundConnection.unbind('state_change', boundStateChangeHandler)
    }
    boundConnection = null
    boundStateChangeHandler = null
    echo.disconnect()
  }

  if (!lifecycleEventBound) {
    lifecycleEventBound = true
    window.addEventListener('echo:token-refreshed', () => {
      resubscribePrivateChannelOnly()
    })
    window.addEventListener('echo:logout', () => {
      stopNotificationListener()
    })
  }

  const startNotificationListener = (user_id?: number | string) => {
    reconnectEcho(userStore.token || localStorage.getItem('token') || '')
    bindWsLifecycleEvents()

    if (!listeningPublic) {
      listeningPublic = true
      subscribePublicChannel()
    }

    if (!user_id) {
      return
    }

    const privateChannelName = `user.${user_id}`

    if (listeningPrivate && subscribedPrivateChannel === privateChannelName) {
      return
    }

    if (subscribedPrivateChannel) {
      echo.leave(subscribedPrivateChannel)
    }

    subscribedPrivateChannel = privateChannelName
    listeningPrivate = true
    subscribePrivateChannel(privateChannelName)
  }

  return {
    startNotificationListener,
    stopNotificationListener,
  }
}
